import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {
  fetchLaunchedTodayRanking,
  fetchLaunchingSoonRanking,
  fetchMarketCapRanking,
  fetch24hrPriceIncreaseRanking,
} from '../services/api';
import {LeaderboardItem } from '../types';
import '../styles/Ranking.css';

const epochs = [
    ['year', 31536000],
    ['month', 2592000],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1]
];

const getDuration = (timeAgoInSeconds: number) => {
    for (let [name, seconds] of epochs) {
        // @ts-ignore
      const interval = Math.floor(timeAgoInSeconds / seconds);
        if (interval >= 1) {
            return {
                interval: interval,
                epoch: name
            };
        }
    }
};

const timeAgo = (date: string) => {
    // @ts-ignore
  const timeAgoInSeconds = Math.floor((new Date() - new Date(date)) / 1000);
    // @ts-ignore
  const {interval, epoch} = getDuration(timeAgoInSeconds);
    const suffix = interval === 1 ? '' : 's';
    return `${interval} ${epoch}${suffix} ago`;
};

const Ranking: React.FC = () => {
  const [launchingSoonData, setLaunchingSoonData] = useState<LeaderboardItem[]>([]);
  const [launchingTodayData, setLaunchingTodayData] = useState<LeaderboardItem[]>([]);
  const [marketCapRankingData, setMarketCapRankingData] = useState<LeaderboardItem[]>([]);
  const [priceIncreaseData, setPriceIncreaseData] = useState<LeaderboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [launchingSoonData, launchingTodayData, marketCapRankingData, priceIncreaseData] = await Promise.all([
          fetchLaunchingSoonRanking(),
          fetchLaunchedTodayRanking(),
          fetchMarketCapRanking(),
          fetch24hrPriceIncreaseRanking(),
        ]);
        setLaunchingSoonData(launchingSoonData);
        setMarketCapRankingData(marketCapRankingData);
        setLaunchingTodayData(launchingTodayData)
        setPriceIncreaseData(priceIncreaseData)
        setLastUpdated(new Date().toLocaleString('en-US', { timeZone: 'UTC' }));
      } catch (err) {
        setError('Failed to fetch ranking data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const handleRowClick = (item: LeaderboardItem) => {
    if (item.StoreUrl) {
      if (item.StoreUrl.startsWith('http://') || item.StoreUrl.startsWith('https://')) {
        // External link
        window.open(item.StoreUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleRowClickInternal = (item: LeaderboardItem) => {
    if (item.ContractAddress) {
      navigate(`/token/${item.ContractAddress}`);
    }
  };

  const getIcon = (index: number) => {
    const icons = ['👑','🥈','🥉']
    if (icons[index]) {
      return icons[index]
    }

    return index + 1
  };


  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="ranking-container">
      <h1 className="ranking-title">Weirdly.fuN <span className="ranking-title-highlight">Rankings</span></h1>
      <p className="ranking-update-time">The data was last updated at {lastUpdated} (UTC)</p>

      <div className="ranking-tables-container-top">
        <div className="ranking-table-wrapper">
          <h2 className="table-title">Next to Mint!</h2>
          <p className="table-subtitle">
            These tokens are in the Mint Queue. See mint estimates below.<br />
            Visit <Link to="/launching" className="launching-soon-link">Launching Soon</Link> to see games that will be added to the Queue next.
          </p>
          <table className="ranking-table">
            <thead>
              <tr>
                <th></th>
                <th>Name (Symbol is TBD)</th>
                <th>Launch Estimation</th>
              </tr>
            </thead>
            <tbody>
              {launchingSoonData.slice(0, 10).map((item, index) => (
                <tr key={index} onClick={() => handleRowClick(item)} className="clickable-row">
                  <td>{getIcon(index)}</td>
                  <td>
                    <img src={`../game-images/${item.LogoUrl}`} alt={item.Name} className="game-logo" />
                    <span className="launching-time-name">{item.Name}</span>
                  </td>
                  <td className="launching-time">{item.LaunchTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ranking-table-wrapper">
          <h2 className="table-title">Recently Launched!</h2>
          <p className="table-subtitle">Can you handle this much alpha?</p>
          <table className="ranking-table">
            <thead>
              <tr>
                <th></th>
                <th>Name (Symbol)</th>
                <th>Price</th>
                <th>Launched</th>
              </tr>
            </thead>
            <tbody>
              {launchingTodayData.map((item, index) => (
                <tr key={index} onClick={() => handleRowClickInternal(item)} className="clickable-row">
                  <td>{getIcon(index)}</td>
                  <td className="table-row-font">
                    <img src={`../game-images/${item.LogoUrl}`} alt={item.Name} className="game-logo" />
                    {item.Name} (${item.Symbol})
                  </td>
                  <td className="table-row-font">${item.CurrentPrice ? item.CurrentPrice : 0.00}</td>
                  <td className="table-row-font">
                    {timeAgo(item.LaunchTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ranking-tables-container">
        <div className="ranking-table-wrapper">
          <h2 className="table-title">24H Price Increase!</h2>
          <p className="table-subtitle">Weirdly.fuN Gainers </p>
          <table className="ranking-table">
            <thead>
              <tr>
                <th></th>
                <th>Name (Symbol)</th>
                <th>Price</th>
                <th>Change%</th>
              </tr>
            </thead>
            <tbody>
              {priceIncreaseData.map((item, index) => (
                <tr key={index} onClick={() => handleRowClickInternal(item)} className="clickable-row">
                  <td>{getIcon(index)}</td>
                  <td className="table-row-font">
                    <img src={`../game-images/${item.LogoUrl}`} alt={item.Name} className="game-logo" />
                    {item.Name} (${item.Symbol})
                  </td>
                  <td className="table-row-font">${item.CurrentPrice ? item.CurrentPrice : 0.00}</td>
                  <td className="table-row-font">
                    <div className="bonding-curve-bar"></div>
                    {item.PercentageChange ? item.PercentageChange.toLocaleString() : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ranking-table-wrapper">
          <h2 className="table-title">Market Cap Ranking</h2>
          <p className="table-subtitle">Weirdly.fuN Market Cap Leaders</p>
          <table className="ranking-table">
            <thead>
              <tr>
                <th></th>
                <th>Name (Symbol)</th>
                <th>MC Change</th>
                <th>BondingCurve%</th>
              </tr>
            </thead>
            <tbody>
              {marketCapRankingData.map((item, index) => (
                <tr key={index} onClick={() => handleRowClickInternal(item)} className="clickable-row">
                  <td>{getIcon(index)}</td>
                  <td className="table-row-font">
                    <img src={`../game-images/${item.LogoUrl}`} alt={item.Name} className="game-logo" />
                    {item.Name} (${item.Symbol})
                  </td>
                  <td className="table-row-font">{item.MarketCapChange ? item.MarketCapChange : 0}%</td>
                  <td className="table-row-font">
                    <div className="bonding-curve-bar" style={{width: `${item.BondingCurveProgress}%`}}></div>
                    {item.BondingCurveProgress}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Ranking;